import {
  ITypeFieldFilterOptions,
  OperatorType,
  PermitFieldType,
} from '~/client/graph'
import { DefaultWorkflowType } from '~/client/src/shared/models/WorkflowType'

export const linkedWorkflowLikeFieldTypes = [
  PermitFieldType.LinkedWorkflow,
  PermitFieldType.Activity,
  PermitFieldType.PrimaryActivity,
]

export const workflowFieldTypes = [
  PermitFieldType.Company,
  PermitFieldType.Requester,
  PermitFieldType.Assignee,
  PermitFieldType.Subscriber,
  PermitFieldType.Location,
  PermitFieldType.Equipment,
  PermitFieldType.StartFinishDates,
  PermitFieldType.Material,
  PermitFieldType.MaterialResource,
  PermitFieldType.PrimaryActivity,
]

export const singleInstancesPermitFields = [...workflowFieldTypes]

export const multiplePermitFieldTypes = [
  PermitFieldType.InputText,
  PermitFieldType.InputTextArea,
  PermitFieldType.InputNumber,
  PermitFieldType.AdditionalDate,
  PermitFieldType.Company,
  PermitFieldType.Requester,
  PermitFieldType.Assignee,
  PermitFieldType.Subscriber,
  PermitFieldType.User,
  PermitFieldType.Location,
  PermitFieldType.Equipment,
  PermitFieldType.Select,
  PermitFieldType.Email,
  PermitFieldType.PhoneNumber,
  PermitFieldType.Material,
  PermitFieldType.MaterialResource,
  PermitFieldType.LocationInformational,
  PermitFieldType.EquipmentInformational,
  PermitFieldType.MaterialInformational,
  PermitFieldType.MaterialResourceInformational,
  PermitFieldType.RichText,
  PermitFieldType.CompanyInformational,
  ...linkedWorkflowLikeFieldTypes,
  PermitFieldType.Signature,
]

export const nestablePermitFields = [
  PermitFieldType.Section,
  PermitFieldType.Subsection,
]

const mainLocationFieldTypes = [
  PermitFieldType.Location,
  PermitFieldType.Equipment,
]

/**
 * The two field types that are a location and nothing else. `locationPermitFieldTypes`
 * also carries the equipment variants, which every location-only branch then has to
 * subtract again - these are the list to reach for when that subtraction is the point.
 */
export const locationOnlyPermitFieldTypes = [
  PermitFieldType.Location,
  PermitFieldType.LocationInformational,
]

export const locationPermitFieldTypes = [
  ...mainLocationFieldTypes,
  PermitFieldType.LocationInformational,
  PermitFieldType.EquipmentInformational,
]

export const conditionalPermitFields = [
  PermitFieldType.Select,
  PermitFieldType.Question,
  PermitFieldType.Checklist,
  PermitFieldType.Material,
  PermitFieldType.MaterialInformational,
  PermitFieldType.MaterialResource,
  PermitFieldType.MaterialResourceInformational,
  ...locationPermitFieldTypes,
]

export const nonHiddeablePermitFieldTypes = [
  PermitFieldType.Section,
  PermitFieldType.Subsection,
  PermitFieldType.Text,
  PermitFieldType.Divider,
  PermitFieldType.InlineMedia,
]

export const selectOptionsPermitFieldTypes = [
  PermitFieldType.Select,
  PermitFieldType.Measure,
]

export const userPermitFieldTypes = [
  PermitFieldType.Requester,
  PermitFieldType.Assignee,
  PermitFieldType.Subscriber,
  PermitFieldType.User,
]

export const companyPermitFieldTypes = [
  PermitFieldType.Company,
  PermitFieldType.CompanyInformational,
]

export const equipmentPermitFieldTypes = [
  PermitFieldType.Equipment,
  PermitFieldType.EquipmentInformational,
]

export const materialPermitFieldTypes = [
  PermitFieldType.Material,
  PermitFieldType.MaterialInformational,
]

export const materialResourcePermitFieldTypes = [
  PermitFieldType.MaterialResource,
  PermitFieldType.MaterialResourceInformational,
]

export const resourcePermitFieldTypes = [
  ...equipmentPermitFieldTypes,
  ...materialResourcePermitFieldTypes,
]

const resourceCreatingPermitFieldTypes = [
  ...resourcePermitFieldTypes,
  ...locationOnlyPermitFieldTypes,
]

export const nonConditionalPermitFieldTypes = [
  ...workflowFieldTypes,
  PermitFieldType.Instructions,
  PermitFieldType.Divider,
  ...nestablePermitFields,
]

export const restrictedTablePermitFieldTypes = [
  ...nonConditionalPermitFieldTypes,
  PermitFieldType.Checklist,
  PermitFieldType.Question,
  PermitFieldType.Table,
  PermitFieldType.Text,
  PermitFieldType.File,
  PermitFieldType.RichText,
  PermitFieldType.InlineMedia,
  ...materialResourcePermitFieldTypes,
]

export const restrictedMaterialResourceAssociatedFieldTypes = [
  ...nonConditionalPermitFieldTypes,
  PermitFieldType.Checklist,
  PermitFieldType.Question,
  PermitFieldType.Table,
  PermitFieldType.Text,
  PermitFieldType.RichText,
  ...materialPermitFieldTypes,
  ...materialResourcePermitFieldTypes,
]

export const defaultNotificationPermitFieldTypes = [
  PermitFieldType.Requester,
  PermitFieldType.Assignee,
  PermitFieldType.Subscriber,
]

export const allNotificationPermitFieldTypes = [
  ...defaultNotificationPermitFieldTypes,
  PermitFieldType.PhoneNumber,
  PermitFieldType.User,
]

export const expandablePermitFieldTypes: PermitFieldType[] = [
  ...linkedWorkflowLikeFieldTypes,
  PermitFieldType.Signature,
  PermitFieldType.Table,
  PermitFieldType.InlineMedia,
  ...equipmentPermitFieldTypes,
  ...materialResourcePermitFieldTypes,
  ...locationPermitFieldTypes,
  ...selectOptionsPermitFieldTypes,
  ...allNotificationPermitFieldTypes,
]

export const nonRemovableFieldsByWorkflowType = new Map<
  string,
  readonly PermitFieldType[]
>([
  [
    DefaultWorkflowType.MaterialTransfer,
    [
      PermitFieldType.Location,
      PermitFieldType.StartFinishDates,
      PermitFieldType.Material,
    ],
  ],
  [
    DefaultWorkflowType.Activity,
    [
      PermitFieldType.Location,
      PermitFieldType.StartFinishDates,
      PermitFieldType.Company,
      PermitFieldType.Assignee,
    ],
  ],
])

export function getNonRemovableFieldTypes(
  workflowTypeString: string,
): readonly PermitFieldType[] {
  const direct = nonRemovableFieldsByWorkflowType.get(workflowTypeString)
  if (direct) return direct
  for (const [key, value] of nonRemovableFieldsByWorkflowType) {
    if (workflowTypeString.startsWith(key)) return value
  }
  return []
}

export function isLinkedWorkflowLikeField(
  type: PermitFieldType | string,
): boolean {
  return linkedWorkflowLikeFieldTypes.includes(type as PermitFieldType)
}

/** A multiple-value field picks its whole set at once; a table cell stays one per slot. */
export function isPickedAsSet(
  field: { isMultiple?: boolean | null } | null | undefined,
  tableId?: string | null,
): boolean {
  return !!field?.isMultiple && !tableId
}

export function isFieldMultiple(isMultiple?: boolean | null): boolean {
  return isMultiple ?? false
}

export function getDefaultFilterOptions(
  type: PermitFieldType,
): ITypeFieldFilterOptions[] {
  if (
    type === PermitFieldType.Activity ||
    type === PermitFieldType.PrimaryActivity
  ) {
    return [
      {
        propertyPath: 'initialType',
        operator: OperatorType.In,
        values: [DefaultWorkflowType.Activity],
      },
    ]
  }
  return []
}

/** A new resource field starts with "Allow Users to create new" off; admins opt in per field. */
export function getDefaultAllowCreateNew(type: PermitFieldType): boolean {
  return !resourceCreatingPermitFieldTypes.includes(type)
}
